import React, { useEffect, useState, useRef } from 'react';
import MySwal from '../../utils/sweetalert';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../config';
import axios from 'axios';
import '../../Styles/Admin/assessment/AssessmentQuestions.css';

const options = [
  "Strongly Agree",
  "Agree",
  "Neutral",
  "Disagree",
  "Strongly Disagree"
];

// Weight of each section in the total score (must add up to 1).
// Names must match the quiz names exactly. A section missing from this
// list falls back to an equal share.
const weights = {
  "Leadership, Trust & Integrity": 0.20,
  "Employee Experience, Belonging & Collaboration": 0.20,
  "Wellbeing, Equity & Fair Opportunity": 0.15,
  "Purpose, Meaningful Work & Achievement": 0.15,
  "Learning, Innovation & Future Readiness": 0.15,
  "Workplace Excellence & Sustainable Impact": 0.15
};

// Total assessment duration in seconds (45 minutes)
const ASSESSMENT_DURATION = 2700;

// localStorage keys used to persist progress across refreshes
const STORAGE_KEYS = {
  answers: 'assessment_answers',
  currentCategoryIdx: 'assessment_current_category_idx',
  maxReachedIdx: 'assessment_max_reached_idx'
};

/* ---------- Small inline icons (no extra image assets needed) ---------- */

const ClockIcon = () => (
  <svg className="aq-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 7.5V12L15 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="10.5" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const ShieldIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3l7 3v5c0 4.5-3 7.7-7 9-4-1.3-7-4.5-7-9V6l7-3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* MOBILE: chevron used by the mobile section dropdown trigger */
const ChevronIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function AssessmentQuestions() {
  const [categories, setCategories] = useState([]);

  // Restore answers from localStorage (falls back to empty object)
  const [answers, setAnswers] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.answers);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Restore which section the user was on
  const [currentCategoryIdx, setCurrentCategoryIdx] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.currentCategoryIdx);
    const parsed = saved ? parseInt(saved, 10) : 0;
    return Number.isNaN(parsed) ? 0 : parsed;
  });

  // Restore how far the user has unlocked
  const [maxReachedIdx, setMaxReachedIdx] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.maxReachedIdx);
    const parsed = saved ? parseInt(saved, 10) : 0;
    return Number.isNaN(parsed) ? 0 : parsed;
  });

  // Timer: derived from the real start timestamp so a refresh doesn't reset it.
  // If no start time exists yet, this is a brand-new attempt — set it now.
  const [timeLeft, setTimeLeft] = useState(() => {
    const startTime = localStorage.getItem('assessment_start_time');
    if (startTime) {
      const elapsed = Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
      const remaining = ASSESSMENT_DURATION - elapsed;
      return remaining > 0 ? remaining : 0;
    }
    localStorage.setItem('assessment_start_time', new Date().toISOString());
    return ASSESSMENT_DURATION;
  });

  // Guards against double-submitting a section (e.g. rapid double-click
  // on "Next Section" / "Finish" firing the network call twice).
  const [submitting, setSubmitting] = useState(false);

  const timerRef = useRef();
  const navigate = useNavigate();
  // Anchor placed at the very top of the page. We scroll THIS into view
  // (instead of window.scrollTo) because scrollIntoView automatically
  // finds and scrolls whichever element is actually the scrolling
  // container — window OR a wrapping div.
  const topAnchorRef = useRef(null);
  // Custom alert state
  const [showAlert, setShowAlert] = useState(false);
  const [unansweredQuestions, setUnansweredQuestions] = useState([]);
  // MOBILE: whether the mobile "current section" dropdown is open
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const mobileNavRef = useRef(null);

  // ---------------------------------------------------------------------
  // Load sections. Only categories that still exist as quizzes are shown,
  // in the same order as the admin Quizzes page. Questions left over from
  // deleted quizzes are ignored.
  // ---------------------------------------------------------------------
  useEffect(() => {
    Promise.all([
      axios.get(`${API_BASE_URL}/api/questions/assessment`),
      axios.get(`${API_BASE_URL}/api/quizzes`)
    ]).then(([qRes, quizRes]) => {
      const activeNames = quizRes.data.map(q => q.category);
      const orderedCategories = activeNames
        .filter(name => name !== "Share Your Thoughts" && qRes.data[name])
        .map(name => ({
          name,
          questions: Array.isArray(qRes.data[name]) ? qRes.data[name] : []
        }));
      setCategories(orderedCategories);
    });
  }, []);

  // Timer logic — recomputed from the stored start time on every tick,
  // so a page refresh can't reset the countdown.
  useEffect(() => {
    timerRef.current = setInterval(() => {
      const startTime = localStorage.getItem('assessment_start_time');
      if (!startTime) return;
      const elapsed = Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
      const remaining = ASSESSMENT_DURATION - elapsed;
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        setTimeLeft(0);
        handleFinish(true); // auto-submit progress
      } else {
        setTimeLeft(remaining);
      }
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist answers whenever they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.answers, JSON.stringify(answers));
  }, [answers]);

  // Persist current section index whenever it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.currentCategoryIdx, String(currentCategoryIdx));
  }, [currentCategoryIdx]);

  // Persist furthest unlocked section whenever it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.maxReachedIdx, String(maxReachedIdx));
  }, [maxReachedIdx]);

  // ---------------------------------------------------------------------
  // SCROLL-TO-TOP FIX (works on both laptop AND mobile)
  // Scroll inside a useEffect keyed on currentCategoryIdx, waiting two
  // animation frames so the new section has actually rendered and painted.
  // ---------------------------------------------------------------------
  useEffect(() => {
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (topAnchorRef.current) {
          topAnchorRef.current.scrollIntoView({ block: 'start', behavior: 'auto' });
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        }
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [currentCategoryIdx]);

  // MOBILE: close the section dropdown when clicking/tapping outside it
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (mobileNavRef.current && !mobileNavRef.current.contains(e.target)) {
        setMobileNavOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, []);

  const flatQuestions = categories.reduce((acc, cat) => {
    cat.questions.forEach(q => acc.push({ ...q, section: cat.name }));
    return acc;
  }, []);

  const currentCategory = categories[currentCategoryIdx] || { name: '', questions: [] };

  const getGlobalStartIndex = () => {
    let idx = 0;
    for (let i = 0; i < currentCategoryIdx; i++) {
      idx += categories[i].questions.length;
    }
    return idx;
  };

  // Save answer with section info
  const handleAnswer = (qid, value) => {
    const questionObj = flatQuestions.find(q => q.id === qid);
    setAnswers({
      ...answers,
      [qid]: { answer: value, section: questionObj.section }
    });
  };

  const handlePrev = () => {
    setCurrentCategoryIdx(i => Math.max(0, i - 1));
  };

  // Sidebar navigation — only sections already reached are clickable
  const handleSectionClick = (idx) => {
    if (idx <= maxReachedIdx) {
      setCurrentCategoryIdx(idx);
    }
  };

  // Scoring calculation — works with whatever sections currently exist.
  // Every section gets equal weight unless it appears in `weights`.
  const calculateScores = () => {
    let scores = {};
    let totalWeightedScore = 0;
    const active = categories.filter(c => c.questions.length > 0);
    active.forEach(cat => {
      const agreeCount = cat.questions.filter(q =>
        answers[q.id]?.answer === "Agree" || answers[q.id]?.answer === "Strongly Agree"
      ).length;
      const percent = (agreeCount / cat.questions.length) * 100;
      scores[cat.name] = percent;
      totalWeightedScore += percent * (weights[cat.name] ?? 1 / active.length);
    });
    return { scores, totalWeightedScore: (totalWeightedScore / 100).toFixed(2) };
  };

  // Submission validation
  const allAnswered = flatQuestions.length > 0 &&
    flatQuestions.every(q => answers[q.id]?.answer);

  // Define these for button logic
  const isFirstCategory = currentCategoryIdx === 0;
  const isLastCategory = currentCategoryIdx === categories.length - 1;

  // Header progress: how far through the sections the user is
  const percentComplete = categories.length > 0
    ? Math.round(((currentCategoryIdx + 1) / categories.length) * 100)
    : 0;

  // Clear all persisted assessment progress from localStorage (call after a successful submit)
  const clearPersistedProgress = () => {
    localStorage.removeItem('assessment_start_time');
    localStorage.removeItem(STORAGE_KEYS.answers);
    localStorage.removeItem(STORAGE_KEYS.currentCategoryIdx);
    localStorage.removeItem(STORAGE_KEYS.maxReachedIdx);
  };

  // ---------------------------------------------------------------------
  // PER-SECTION SUBMISSION
  // Each section is submitted to POST /api/assessment-taken/section as soon
  // as it's completed (on "Next Section", and again on "Finish"), so admin
  // reports can show partial progress.
  // ---------------------------------------------------------------------

  // Builds the payload for one completed section: just that section's
  // own answers plus its own agree/strongly-agree percentage score.
  const buildSectionPayload = (categoryIdx) => {
    const cat = categories[categoryIdx];
    const sectionAnswers = {};
    cat.questions.forEach((q) => {
      if (answers[q.id]?.answer) {
        sectionAnswers[q.id] = answers[q.id].answer;
      }
    });
    const agreeCount = cat.questions.filter(
      (q) =>
        answers[q.id]?.answer === 'Agree' ||
        answers[q.id]?.answer === 'Strongly Agree'
    ).length;
    const score =
      cat.questions.length > 0 ? (agreeCount / cat.questions.length) * 100 : 0;
    return { section: cat.name, answers: sectionAnswers, score };
  };

  // Sends one section to the backend. isFinal=true only on the very last
  // section, since that's the only point the overall weighted total_score
  // can actually be computed.
  const submitSection = async (categoryIdx, { isFinal = false } = {}) => {
    const { section, answers: sectionAnswers, score } = buildSectionPayload(categoryIdx);
    const startDate = localStorage.getItem('assessment_start_time');
    const finishDate = new Date().toISOString();
    let totalScore = null;
    let duration = '';
    if (startDate) {
      const s = new Date(startDate);
      const e = new Date(finishDate);
      if (!isNaN(s) && !isNaN(e)) {
        duration = `${Math.round((e - s) / 60000)} Mins`;
      }
    }
    if (isFinal) {
      const { totalWeightedScore } = calculateScores();
      totalScore = totalWeightedScore;
    }
    await axios.post(`${API_BASE_URL}/api/assessment-taken/section`, {
      employee_email: localStorage.getItem('employee_email'),
      section,
      score,
      start_date: startDate,
      finish_date: finishDate,
      duration,
      answers: sectionAnswers,
      is_final: isFinal,
      total_score: totalScore,
    });
  };

  // "Next Section": (a) requires the current section to be fully answered,
  // (b) submits that section immediately, then (c) advances.
  const handleNext = async () => {
    const unanswered = currentCategory.questions.filter((q) => !answers[q.id]?.answer);
    if (unanswered.length > 0) {
      MySwal.fire({
        icon: 'warning',
        title: 'Please answer all questions in this section before continuing.',
        confirmButtonText: 'OK',
      }).then(() => {
        const el = document.getElementById(`question-${unanswered[0].id}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
      return;
    }

    if (submitting) return;
    setSubmitting(true);
    try {
      await submitSection(currentCategoryIdx, { isFinal: false });
      setCurrentCategoryIdx((i) => {
        const next = Math.min(categories.length - 1, i + 1);
        setMaxReachedIdx((m) => Math.max(m, next));
        return next;
      });
    } catch (err) {
      MySwal.fire({
        icon: 'error',
        title: 'Error saving this section',
        text: err.response?.data?.error || 'Please try again.',
        confirmButtonText: 'OK',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Submission handler
  const handleFinish = async (auto = false) => {
    // For auto-submit, allow incomplete answers
    if (!auto && !allAnswered) {
      // Find unanswered questions
      const unanswered = flatQuestions.filter(q => !answers[q.id]?.answer);
      // Prepare section/category and question number only
      const unansweredDisplay = unanswered.map(q => {
        const section = q.section || '';
        // Find the global index of this question in flatQuestions
        const globalIdx = flatQuestions.findIndex(qq => qq.id === q.id);
        const qNum = globalIdx >= 0 ? globalIdx + 1 : '?';
        return `<b>${section}</b> - ${qNum}`;
      });
      MySwal.fire({
        icon: 'warning',
        title: 'Please answer all questions before submitting.',
        html: `<div style=\"text-align:left;max-height:200px;overflow:auto;\"><b>Unanswered:</b><ul style='margin:8px 0 0 18px;padding:0;'>${unansweredDisplay.map(u => `<li style='margin-bottom:4px;'>${u}</li>`).join('')}</ul></div>`,
        confirmButtonText: 'OK'
      }).then(() => {
        // Scroll to the first unanswered question
        if (unanswered.length > 0) {
          const firstId = unanswered[0].id;
          const el = document.getElementById(`question-${firstId}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.focus?.();
          }
        }
      });
      return;
    }

    if (submitting) return;
    setSubmitting(true);
    try {
      // Submits the current (final) section. For an auto-submit (timer ran
      // out) this is whichever section the employee was on, with only its
      // answered questions saved.
      await submitSection(currentCategoryIdx, { isFinal: true });
      clearPersistedProgress();
      navigate('/assessment/thank-you', { replace: true });
    } catch (err) {
      MySwal.fire({
        icon: 'error',
        title: 'Error submitting assessment',
        text: err.response?.data?.error || 'Unknown error',
        confirmButtonText: 'OK'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Timer display
  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  if (!categories.length || !categories.some(cat => cat.questions.length > 0)) {
    return (
      <div className="assessment-questions-bg">
        <h2 className="assessment-questions-title">
          Assessment Questions
        </h2>
        <div className="assessment-questions-empty">
          Questions will appear here once added in the admin panel.
        </div>
      </div>
    );
  }

  return (
    <div className="aq-page">
      {/* Invisible anchor at the very top of the page — scrollIntoView is
          called on this element (not window) so navigation always lands
          at the true top, regardless of which ancestor actually scrolls. */}
      <div ref={topAnchorRef} style={{ height: 0, margin: 0, padding: 0 }} />

      {/* ================= MOBILE ONLY (hidden on desktop via CSS) ================= */}
      {/* Mobile header: logo + timer on one row, heading/subheading below */}
      <header className="aq-mobile-header">
        <div className="aq-mobile-header-top">
          <img src="/New_logo.jpg" alt="Outstanding Workplaces" className="aq-mobile-logo" />
          <div className="aq-mobile-timer-pill">
            <ClockIcon />
            <span>{formatTime(timeLeft)}</span>
          </div>
        </div>
        <div className="aq-mobile-header-titles">
          <h1 className="aq-mobile-header-title">Employee Assessment</h1>
          <p className="aq-mobile-header-subtitle">Your feedback helps us build a better workplace for everyone.</p>
        </div>
      </header>

      {/* Mobile progress: "Section X of Y" / percent + progress bar */}
      <div className="aq-mobile-progress">
        <div className="aq-mobile-progress-text">
          <span>Section {currentCategoryIdx + 1} of {categories.length}</span>
          <span className="aq-mobile-progress-percent">{percentComplete}% Completed</span>
        </div>
        <div className="aq-mobile-progress-bar">
          <div className="aq-mobile-progress-fill" style={{ width: `${percentComplete}%` }} />
        </div>
      </div>
      {/* =============== END MOBILE ONLY header block =============== */}

      {/* ---------- Top header (desktop / tablet) ---------- */}
      <header className="aq-header">
        <div className="aq-header-left">
          <img src="/New_logo.jpg" alt="Outstanding Workplaces" className="aq-logo" />
          <div className="aq-header-titles">
            <h1 className="aq-header-title">Employee Assessment</h1>
            <p className="aq-header-subtitle">Your feedback helps us build a better workplace for everyone.</p>
          </div>
        </div>
        <div className="aq-header-right">
          <div className="aq-timer-pill">
            <ClockIcon />
            <span>{formatTime(timeLeft)}</span>
          </div>
          <div className="aq-header-progress">
            <div className="aq-header-progress-text">
              <span>Section {currentCategoryIdx + 1} of {categories.length}</span>
              <span className="aq-header-progress-percent">{percentComplete}% Completed</span>
            </div>
            <div className="aq-header-progress-bar">
              <div className="aq-header-progress-fill" style={{ width: `${percentComplete}%` }} />
            </div>
          </div>
        </div>
      </header>

      <div className="aq-body">
        {/* ---------- Left sidebar (25%) ---------- */}
        <aside className="aq-sidebar">
          {/* ================= MOBILE ONLY: section dropdown (replaces the list below) ================= */}
          <div className="aq-mobile-section-select" ref={mobileNavRef}>
            <button
              type="button"
              className="aq-mobile-section-trigger"
              onClick={() => setMobileNavOpen(o => !o)}
              aria-expanded={mobileNavOpen}
            >
              <span className="aq-mobile-section-number">{currentCategoryIdx + 1}</span>
              <span className="aq-mobile-section-info">
                <span className="aq-mobile-section-name">{currentCategory.name}</span>
                <span className="aq-mobile-section-count">{currentCategory.questions.length} Questions</span>
              </span>
              <ChevronIcon className={`aq-mobile-section-chevron${mobileNavOpen ? ' open' : ''}`} />
            </button>

            {mobileNavOpen && (
              <div className="aq-mobile-section-list">
                {categories.map((cat, idx) => {
                  const locked = idx > maxReachedIdx;
                  const active = idx === currentCategoryIdx;
                  return (
                    <button
                      key={cat.name}
                      type="button"
                      className={`aq-mobile-section-item${active ? ' active' : ''}${locked ? ' locked' : ''}`}
                      onClick={() => {
                        if (!locked) {
                          handleSectionClick(idx);
                          setMobileNavOpen(false);
                        }
                      }}
                      disabled={locked}
                    >
                      <span className="aq-mobile-section-item-number">{idx + 1}</span>
                      <span className="aq-mobile-section-item-info">
                        <span className="aq-mobile-section-item-name">{cat.name}</span>
                        <span className="aq-mobile-section-item-count">{cat.questions.length} Questions</span>
                      </span>
                      {locked && <LockIcon className="aq-section-lock" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {/* =============== END MOBILE ONLY section dropdown =============== */}

          <div className="aq-sections-card">
            <h3 className="aq-sections-heading">ASSESSMENT SECTIONS</h3>
            {categories.map((cat, idx) => {
              const locked = idx > maxReachedIdx;
              const active = idx === currentCategoryIdx;
              const reached = idx <= maxReachedIdx; // section already visited/unlocked
              return (
                <button
                  key={cat.name}
                  type="button"
                  className={`aq-section-item${active ? ' active' : ''}${locked ? ' locked' : ''}${reached ? ' reached' : ''}`}
                  onClick={() => handleSectionClick(idx)}
                  disabled={locked}
                >
                  <span className="aq-section-number">{idx + 1}</span>
                  <span className="aq-section-info">
                    <span className="aq-section-name">{cat.name}</span>
                    <span className="aq-section-count">{cat.questions.length} Questions</span>
                  </span>
                  {locked && <LockIcon className="aq-section-lock" />}
                </button>
              );
            })}
          </div>

          <div className="aq-anon-card">
            <ShieldIcon className="aq-anon-icon" />
            <div>
              <p className="aq-anon-title">Your responses are anonymous</p>
              <p className="aq-anon-text">Your feedback is confidential and cannot be traced back to you.</p>
            </div>
          </div>
        </aside>

        {/* ---------- Right content (75%) ---------- */}
        <main className="aq-main">
          <div className="aq-category-card">
            <h2 className="aq-category-heading">{currentCategory.name}</h2>
            <div className="aq-divider" />
            <div className="aq-category-sub">
              <span>{currentCategory.questions.length} of {flatQuestions.length} questions</span>
              <div className="aq-progress-dots">
                {categories.map((c, i) => (
                  <span key={c.name} className={`aq-dot${i <= currentCategoryIdx ? ' filled' : ''}`} />
                ))}
              </div>
            </div>
          </div>

          {currentCategory.questions.map((q, idx) => (
            <div className="aq-question-card" id={`question-${q.id}`} tabIndex={-1} key={q.id}>
              <div className="aq-question-top">
                <span className="aq-question-number">
                  {getGlobalStartIndex() + idx + 1}
                </span>
                <p className="aq-question-text">
                  {q.question}
                  <span className="aq-required">*</span>
                </p>
              </div>

              <div className="aq-options">
                {options.map(opt => {
                  const selected = answers[q.id]?.answer === opt;
                  return (
                    <label key={opt} className={`aq-option${selected ? ' selected' : ''}`}>
                      <input
                        type="radio"
                        name={`q_${q.id}`}
                        value={opt}
                        checked={selected}
                        onChange={() => handleAnswer(q.id, opt)}
                      />
                      <span className="aq-option-circle" />
                      <span className="aq-option-label">{opt}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="aq-actions">
            <button
              className="aq-btn aq-btn-prev"
              onClick={handlePrev}
              disabled={isFirstCategory || submitting}
            >
              ← Previous
            </button>
            {isLastCategory ? (
              <button
                className="aq-btn aq-btn-finish"
                onClick={() => handleFinish(false)}
                disabled={submitting}
              >
                {submitting ? 'Submitting…' : 'Finish'}
              </button>
            ) : (
              <button
                className="aq-btn aq-btn-next"
                onClick={handleNext}
                disabled={submitting}
              >
                {submitting ? 'Saving…' : 'Next Section →'}
              </button>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default AssessmentQuestions;